import React, { useId, useState } from 'react';
import { ItemIcon } from '../../../components/ItemIcon';
import { TomeButton } from '../../../primitives/tome';
import { checkGoldAmount, formatGoldCount, type GoldDirection } from './bankGoldAmount';

/**
 * One purse of the Bank: the balance in large gilt figures, a typed amount,
 * an All chip that fills the field with the whole balance, and the action.
 * Enter submits. The field clears after a send; the server's answer arrives
 * as `notice` (useBank's goldNotice for this direction).
 */
export interface BankGoldFormProps {
  direction: GoldDirection;
  /** What can move: the carried purse (deposit) or the vault balance (withdraw). */
  balance: number;
  disabled?: boolean;
  onSubmit: (amount: number) => void;
  /** The server's last answer for this direction. */
  notice?: { tone: 'ok' | 'error'; text: string } | null;
}

const COPY: Record<GoldDirection, { place: string; action: string; field: string }> = {
  deposit: { place: 'Carried', action: 'Deposit', field: 'Gold to deposit' },
  withdraw: { place: 'In the vault', action: 'Withdraw', field: 'Gold to withdraw' },
};

export const BankGoldForm: React.FC<BankGoldFormProps> = ({ direction, balance, disabled = false, onSubmit, notice }) => {
  const [text, setText] = useState('');
  const id = useId();
  const copy = COPY[direction];
  const check = checkGoldAmount(text, balance, direction);
  const problem = !check.ok && !check.empty ? check.problem : '';
  const message = problem || notice?.text || '';
  const tone = problem ? 'error' : notice?.tone ?? 'ok';

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (disabled || !check.ok) return;
    onSubmit(check.amount);
    setText('');
  };

  return <form className="bank-purse" data-testid={`bank-gold-${direction}`} data-direction={direction} onSubmit={submit} noValidate>
    <div className="bank-purse-balance">
      <span className="bank-purse-coin" aria-hidden="true"><ItemIcon itemId="gold" size={22} /></span>
      <span className="bank-purse-amount" data-testid={`bank-gold-${direction}-balance`}>{formatGoldCount(balance)}</span>
      <span className="bank-purse-place">{copy.place}</span>
    </div>
    <div className="bank-purse-controls">
      <input id={id} className="bank-purse-input" type="text" inputMode="numeric" autoComplete="off" spellCheck={false}
        placeholder="Amount" aria-label={copy.field} value={text} disabled={disabled}
        aria-invalid={problem ? true : undefined} aria-describedby={message ? `${id}-note` : undefined}
        onChange={event => setText(event.target.value)} />
      <TomeButton tone="secondary" cut="clean" disabled={disabled || balance <= 0}
        aria-label={`Fill all ${formatGoldCount(balance)} gold`} onClick={() => setText(String(balance))}>All</TomeButton>
      <TomeButton type="submit" cut="clean" disabled={disabled || !check.ok}>{copy.action}</TomeButton>
    </div>
    <p id={`${id}-note`} className="bank-purse-note" data-tone={tone} role="status" aria-live="polite">{message}</p>
  </form>;
};
